import type { ResultListStateQuery } from '../state/state.types';
import type { VoyagerFacetConfig } from './VoyagerSearchService.types';
import { buildFacetExcludeTagSections, buildFacetParams } from './facetHelpers';
import { buildTreeNsdiFilterIdFromHierarchyBase } from './treeHelpers';

const METADATA_FORMAT_IDS = [
  'dcat dataset',
  'iso 19115',
  'application/rss+xml',
];

// Legacy scope tokens carried by older bookmarked URLs. Configured search-scope
// buttons express their filter via pill filters or `scopeFq`, and store a
// slugified button id (e.g. "metadata-only") in `searchScope` purely for
// active-state tracking — those must NOT be interpreted here, otherwise the
// slug falls through to the "exclude metadata" expression and contradicts the
// button's own metadata pills (numFound: 0).
const buildFormatScopeExpression = (
  scope: string | undefined,
): string | null => {
  const formatGroup = METADATA_FORMAT_IDS.map(
    (value) => `"${escapeSolrFilterValue(value)}"`,
  ).join(' OR ');

  if (scope === 'metadata') {
    return `format:(${formatGroup})`;
  }

  if (scope === 'data-items') {
    return `(*:* AND -format:(${formatGroup}))`;
  }

  // Configured/renamed scope buttons are represented by their own pill
  // filters or raw scopeFq, so no legacy format expression should be applied.
  return null;
};

export const mapSortField = (sortBy: string): string | null => {
  const sortFieldMap: Record<string, string> = {
    relevancy: 'score',
    date: 'fd__acquired',
    releaseYear: 'fd_publish_date',
    title: 'title',
    changeDate: 'modified',
    publicationDate: 'fd_publish_date',
    rankCountry: 'grp_Country',
  };

  return sortFieldMap[sortBy] || 'fd__acquired';
};

/**
 * Escapes backslashes and double quotes to keep quoted Solr fq values valid.
 * This helper is only for values interpolated inside double quotes.
 */
const escapeSolrFilterValue = (value: string): string =>
  value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

interface BuildSearchUrlArgs {
  query: ResultListStateQuery;
  options?: { includeFacets?: boolean };
  searchUrl: string;
  configId: string;
  facetConfig: VoyagerFacetConfig;
  resultFields: string;
}

export const buildSearchUrl = ({
  query,
  options = {},
  searchUrl,
  configId,
  facetConfig,
  resultFields,
}: BuildSearchUrlArgs): string => {
  const params = new URLSearchParams();

  if (query.voyagerSort) {
    params.set('sort', query.voyagerSort);
  } else {
    const sortField = mapSortField(query.sortBy || 'date');
    const sortDir = query.sortOrder === 'asc' ? 'asc' : 'desc';
    if (sortField) {
      params.set('sort', `${sortField} ${sortDir}`);
    }
  }

  params.set('basemap', query.basemap || 'ESRI Street Map');
  params.set('disp', query.displayId || configId);

  const start = (query.pageIndex || 0) * (query.pageSize || 48);
  const rows = query.pageSize || 48;
  params.set('start', start.toString());
  params.set('fl', resultFields);
  params.set('rows', rows.toString());
  params.set('extent.bbox', 'true');
  params.set('block', 'true');
  params.set('voyager.config.id', configId);
  params.set('wt', 'json');

  if (query.search) {
    params.set('q', query.search);
  }

  const hierarchyBaseFilterId = buildTreeNsdiFilterIdFromHierarchyBase(
    facetConfig.hierarchyTreeBase,
  );
  const hasHierarchyBaseFilter =
    hierarchyBaseFilterId &&
    query.filters?.some(
      (filter) =>
        filter.isChecked &&
        filter.sectionId === 'tree_nsdi' &&
        filter.id === hierarchyBaseFilterId,
    );

  if (hierarchyBaseFilterId && !hasHierarchyBaseFilter) {
    params.append(
      'fq',
      `tree_nsdi:"${escapeSolrFilterValue(hierarchyBaseFilterId)}"`,
    );
  }

  const filtersBySection = new Map<string, string[]>();
  query.filters?.forEach((filter) => {
    if (!filter.isChecked || !filter.sectionId || !filter.id) {
      return;
    }

    const currentValues = filtersBySection.get(filter.sectionId) || [];
    currentValues.push(filter.id);
    filtersBySection.set(filter.sectionId, currentValues);
  });

  // Sections that declare a self-referencing Solr exclude tag must have their
  // fq tagged so the section keeps showing sibling values after a selection,
  // while the active keyword search and other filters still narrow the counts.
  const excludeTagSections = buildFacetExcludeTagSections(facetConfig);

  filtersBySection.forEach((values, sectionId) => {
    const uniqueValues = Array.from(new Set(values));
    const tagPrefix = excludeTagSections.has(sectionId)
      ? `{!tag=${sectionId}}`
      : '';
    if (uniqueValues.length === 1) {
      params.append(
        'fq',
        `${tagPrefix}${sectionId}:"${escapeSolrFilterValue(uniqueValues[0])}"`,
      );
      return;
    }

    const valueGroup = uniqueValues
      .map((value) => `${sectionId}:"${escapeSolrFilterValue(value)}"`)
      .join(' OR ');
    params.append('fq', `${tagPrefix}(${valueGroup})`);
  });

  // Prefer an admin-configured raw fq expression from the selected scope button.
  // Fall back to the legacy metadata/data-items format expression for older URLs.
  if (query.scopeFq && query.scopeFq.trim()) {
    params.append('fq', query.scopeFq.trim());
  } else {
    const scopeExpression = buildFormatScopeExpression(query.searchScope);
    if (scopeExpression) {
      params.append('fq', scopeExpression);
    }
  }

  if (query.spatialFilter?.bbox && query.spatialFilter.bbox.length >= 2) {
    const bbox = query.spatialFilter.bbox;
    const minLon = Math.min(...bbox.map((p) => p.longitude));
    const maxLon = Math.max(...bbox.map((p) => p.longitude));
    const minLat = Math.min(...bbox.map((p) => p.latitude));
    const maxLat = Math.max(...bbox.map((p) => p.latitude));
    params.set('place', `${minLon},${minLat},${maxLon},${maxLat}`);
    params.set('place.op', 'within');
  }

  if (options.includeFacets) {
    params.set('facet', 'true');
    params.set('facet.mincount', String(facetConfig.minCount || 1));
    params.set('facet.limit', String(facetConfig.limit || 11));
    facetConfig.fields.forEach(({ field }) => {
      params.append('facet.field', field);
    });
    Object.entries(buildFacetParams(facetConfig)).forEach(([key, value]) => {
      params.set(key, value);
    });
  }

  Object.entries(query.facetParams || {}).forEach(([key, value]) => {
    params.set(key, value);
  });

  return `${searchUrl}?${params.toString()}`;
};
